import { PortalLink } from "../components/PortalLink";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Download, ExternalLink, ListChecks } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { StatusTag } from "../components/Drawing";
import { api, apiBlob, apiErrorMessage } from "../api/client";
import { saveBlob, fileNameSafe } from "../lib/download";
import { LoadingBlock } from "../components/Spinner";
import { MarkdownDocument } from "../components/MarkdownDocument";
import { AuditBriefing } from "../components/AuditBriefing";
import {
  TRAINING_KINDS, labelOf, Field, Check, EmptyState, input, textarea, secondaryBtn, dangerBtn, fmtDay, fmtDateOnly, fmtDateTime,
} from "./resilience/shared";

// My tasks: what this member has been asked to do personally. Every member sees it, viewers
// included; each action is theirs alone (the API keys every row by the caller).

interface AckTask {
  id: string;
  status: "pending" | "acknowledged" | "excused";
  openedAt: string | null;
  acknowledgedAt: string | null;
  statement: string;
  campaign: { id: string; title: string; message: string | null; documentTitle: string; documentVersion: number; dueAt: string; evidenceDocumentId: string | null };
  documentAvailable: boolean;
  overdue: boolean;
}

type Standing = "completed" | "excused" | "awaiting_confirmation" | "overdue" | "due_soon" | "pending";

interface TrainingTask {
  id: string;
  status: "pending" | "submitted" | "completed" | "excused";
  standing: Standing;
  dueAt: string;
  cycle: number;
  submittedAt: string | null;
  completedAt: string | null;
  score: number | null;
  assignment: { id: string; title: string; kind: string; provider: string | null; url: string | null; description: string | null; completionMode: "self_attest" | "admin_confirm"; passMark: number | null };
}

interface ApprovalTask {
  id: string;
  documentTitle: string;
  documentVersion: number;
  requestNote: string | null;
  requestedBy: string | null;
  selfApproved: boolean;
  reviewIntervalMonths: number;
  createdAt: string;
  documentAvailable: boolean;
}

/** A corrective action the caller owns, or one whose effectiveness they should review. */
interface ActionTask {
  id: string;
  displayId: number | null;
  title: string;
  status: string;
  priority: string;
  dueDate: string | null;
  effectivenessDueAt: string | null;
  overdue: boolean;
  href: string;
  standing?: "due" | "overdue";
}

/** A step of a corrective action given to the caller (CorrectiveActionMilestone.ownerUserId). */
interface StepTask {
  id: string;
  title: string;
  dueDate: string | null;
  overdue: boolean;
  stepNumber: number;
  stepCount: number;
  action: { id: string; displayId: number | null; title: string; status: string };
  href: string;
}

/** A technical scope document the caller was asked to confirm (routes/technicalScopes.routes.ts). */
interface ScopeReviewTask {
  id: string;
  version: number;
  note: string | null;
  requestedAt: string;
  requestedBy: string | null;
  selfReviewed: boolean;
  scope: {
    id: string; displayId: number; ref: string; title: string; workType: string; workTypeLabel: string;
    plannedStart: string | null; plannedEnd: string | null; performer: string | null;
  };
}

interface Tasks {
  acknowledgements: AckTask[]; training: TrainingTask[]; approvals: ApprovalTask[];
  // Optional so an API that predates them still renders the page.
  correctiveActions?: ActionTask[]; effectivenessReviews?: ActionTask[]; actionSteps?: StepTask[];
  scopeReviews?: ScopeReviewTask[];
  accessReviews?: AccessReviewTask[];
  evidenceRenewals?: RenewalTask[];
  policyExceptions?: PolicyExceptionTask[];
  changes?: ChangeTask[];
  attestations?: AttestationTask[];
}

/** A control the caller owns whose attestation is due, overdue or "not operating" (backend lib/controlAttestations.ts). */
interface AttestationTask {
  controlId: string; controlLabel: string; cycleMonths: number; nextDueAt: string;
  standing: "awaiting_first" | "current" | "due_soon" | "lapsed" | "not_operating";
  last: { outcome: string; attestedAt: string; note: string | null } | null; href: string;
}

/** A change the caller was named as approver of (backend routes/changeRegister.routes.ts, lib/changeRegister.ts). */
interface ChangeTask {
  id: string; ref: string; title: string; description: string; changeType: string; changeTypeLabel: string;
  rollbackPlan: string | null; plannedDate: string | null; implementedAt: string | null;
  /** An emergency change already made, decided after the fact. */
  retrospective: boolean;
  requestedBy: string | null; selfApproved: boolean; assets: string[];
}

/** An evidence document the caller renews, due for renewal or out of date (backend lib/evidenceValidity.ts). */
interface RenewalTask {
  id: string; fileName: string; typeLabel: string; evidenceDate: string; validUntil: string; lapsed: boolean; href: string;
}

interface PolicyExceptionTask {
  id: string; ref: string; title: string; policyTitle: string; ruleReference: string | null; justification: string; compensatingControls: string;
  startsAt: string; expiresAt: string; requestedBy: string | null; selfApproved: boolean; renewal: boolean;
  risk: { ref: string | null; title: string; score: number | null }; covers: string[];
}

interface AccessReviewTask {
  id: string; resourceKind: "group" | "app"; resourceName: string; principalName: string; principalEmail: string | null;
  sourceLabel: string; askedAs: string; selfReviewed: boolean; overdue: boolean;
  campaign: { id: string; ref: string; title: string; dueAt: string };
}

const sectionTitle: React.CSSProperties = { ...text.sectionTitle, margin: "0 0 10px" };
const isHttps = (u: string | null): u is string => { try { return !!u && new URL(u).protocol === "https:"; } catch { return false; } };

function Pill({ tone, children }: { tone: "success" | "warning" | "danger" | "accent" | "muted"; children: React.ReactNode }) {
  const s = {
    success: { bg: theme.color.successBg, color: theme.color.successText },
    warning: { bg: theme.color.warningBg, color: theme.color.warningText },
    danger:  { bg: theme.color.dangerBg,  color: theme.color.dangerText },
    accent:  { bg: theme.color.accentBg,  color: theme.color.accentText },
    muted:   { bg: theme.color.panelAlt,  color: theme.color.muted },
  }[tone];
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{children}</span>;
}

// The tab is opened on the click itself and pointed at the link once the API answers: a tab
// opened after a network round trip is no longer "from the click" and pop-up blockers stop it.
async function openLink(path: string) {
  const tab = window.open("about:blank", "_blank");
  try {
    const res = await api.post<{ url: string }>(path);
    if (tab) {
      tab.opener = null;
      tab.location.href = res.url;
    } else {
      window.open(res.url, "_blank", "noopener");
    }
  } catch (e) {
    tab?.close();
    throw e;
  }
}

export function MyTasksPage() {
  const [tasks, setTasks] = useState<Tasks | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setTasks(await api.get<Tasks>("/me/tasks")); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load your tasks")); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!tasks && !error) return <div className="page-pad"><LoadingBlock /></div>;

  const acks = tasks ? [...tasks.acknowledgements].sort((a, b) => (a.status === "pending" ? 0 : 1) - (b.status === "pending" ? 0 : 1) || new Date(a.campaign.dueAt).getTime() - new Date(b.campaign.dueAt).getTime()) : [];
  const training = tasks ? [...tasks.training].sort((a, b) => (a.status === "completed" ? 1 : 0) - (b.status === "completed" ? 1 : 0)) : [];
  const approvals = tasks?.approvals ?? [];
  const actions = tasks?.correctiveActions ?? [];
  const reviews = tasks?.effectivenessReviews ?? [];
  const scopeReviews = tasks?.scopeReviews ?? [];
  const steps = tasks?.actionSteps ?? [];
  const accessReviews = tasks?.accessReviews ?? [];
  const renewals = tasks?.evidenceRenewals ?? [];
  const exceptions = tasks?.policyExceptions ?? [];
  const changes = tasks?.changes ?? [];
  const attestations = tasks?.attestations ?? [];
  const outstanding = acks.filter((a) => a.status === "pending").length + training.filter((t) => t.status === "pending").length + approvals.length
    + actions.length + reviews.length + scopeReviews.length + accessReviews.length + steps.length + renewals.length + exceptions.length + changes.length + attestations.length;

  return (
    <div className="page-pad">
      <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
        <ListChecks size={20} /> My tasks
      </h1>
      <p style={{ ...pageSubtitle, margin: "0 0 20px", maxWidth: 760 }}>
        What your organisation has asked you to do: policies to read and confirm, training to complete, policies waiting for your approval, the corrective actions you own, the steps of actions given to you, technical scopes to confirm, people's access to review and evidence you renew. Only you can do these, and each one is recorded against your name.
      </p>

      {error && <div style={{ ...card, padding: 12, marginBottom: 16, ...text.body, color: theme.color.dangerText }}>{error}</div>}

      <AuditBriefing />

      {tasks && outstanding > 0 && (
        <div style={{ ...text.body, fontWeight: 600, margin: "-8px 0 16px" }}>
          {outstanding} {outstanding === 1 ? "thing is" : "things are"} waiting for you.
        </div>
      )}

      {tasks && (
        <div style={{ display: "grid", gap: 24 }}>
          {outstanding === 0 && (
            <div style={card}>
              <EmptyState title="You are all caught up" detail="Nothing is waiting for you. When a policy is sent to you, training is assigned, or you are asked to approve something or confirm a technical scope, it appears here and you are emailed." />
            </div>
          )}

          {acks.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Policies to read</h2>
              <div style={{ display: "grid", gap: 12 }}>
                {acks.map((a) => <AckCard key={a.id} a={a} onDone={load} />)}
              </div>
            </section>
          )}

          {training.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Training to complete</h2>
              <div style={{ display: "grid", gap: 12 }}>
                {training.map((t) => <TrainingCard key={t.id} t={t} onDone={load} />)}
              </div>
            </section>
          )}
          {approvals.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Approvals waiting for you</h2>
              <div style={{ display: "grid", gap: 12 }}>
                {approvals.map((a) => <ApprovalCard key={a.id} a={a} onDone={load} />)}
              </div>
            </section>
          )}

          {scopeReviews.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Technical scopes to confirm</h2>
              <p style={{ margin: "0 0 10px", ...text.hint, maxWidth: 760 }}>
                Someone is about to carry out work on your systems. Check the technical detail is right, especially anything marked "[to be confirmed by IT]", then confirm it or say what needs changing.
              </p>
              <div style={{ display: "grid", gap: 12 }}>
                {scopeReviews.map((r) => <ScopeReviewCard key={r.id} r={r} onDone={load} />)}
              </div>
            </section>
          )}

          {exceptions.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Policy exceptions to decide</h2>
              <p style={{ margin: "0 0 10px", ...text.hint, maxWidth: 760 }}>
                You were named as the approver. Approving records the risk acceptance on the risk register in your name until the expiry date; your risk appetite rules decide how long a risk can be accepted.
              </p>
              <div style={{ display: "grid", gap: 12 }}>
                {exceptions.map((e) => <PolicyExceptionCard key={e.id} e={e} onDone={load} />)}
              </div>
            </section>
          )}

          {changes.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Changes to decide</h2>
              <p style={{ margin: "0 0 10px", ...text.hint, maxWidth: 760 }}>
                You were named as the approver. An emergency change may already have been made; approve it after the fact, or refuse it so that it is rolled back.
              </p>
              <div style={{ display: "grid", gap: 12 }}>
                {changes.map((c) => <ChangeDecisionCard key={c.id} c={c} onDone={load} />)}
              </div>
            </section>
          )}

          {attestations.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Controls to attest</h2>
              <p style={{ margin: "0 0 10px", ...text.hint, maxWidth: 760 }}>
                You own these controls. Confirm each still operates as described, or say what is not working. Your answer is recorded against your name; it does not change the control's status.
              </p>
              <div style={{ display: "grid", gap: 12 }}>
                {attestations.map((a) => <AttestationCard key={a.controlId} a={a} onDone={load} />)}
              </div>
            </section>
          )}

          {accessReviews.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Access to review</h2>
              <p style={{ margin: "0 0 10px", ...text.hint, maxWidth: 760 }}>
                Does each person still need this group or application for their work? Keep or remove it and say why. Nothing changes in the directory from here: a removal goes to the person who manages access as a corrective action.
              </p>
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                {accessReviews.map((r, i) => <AccessReviewRow key={r.id} r={r} first={i === 0} onDone={load} />)}
              </div>
            </section>
          )}

          {renewals.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Evidence to renew</h2>
              <p style={{ margin: "0 0 10px", ...text.hint, maxWidth: 760 }}>
                Dated records you look after. Each counts as current evidence for a set period; arrange the next one and upload it as a new version of the document. An out-of-date one stays on file but no longer counts.
              </p>
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                {renewals.map((d, i) => (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderTop: i === 0 ? "none" : "1px solid var(--c-line)", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <Link to={d.href} style={{ ...text.body, fontWeight: 600, color: theme.color.accentText, textDecoration: "none" }}>{d.fileName}</Link>
                      <div style={text.hint}>{d.typeLabel}, dated {fmtDateOnly(d.evidenceDate)}</div>
                    </div>
                    <Pill tone={d.lapsed ? "danger" : "warning"}>{d.lapsed ? "Out of date since" : "Renew by"} {fmtDateOnly(d.validUntil)}</Pill>
                  </div>
                ))}
              </div>
            </section>
          )}

          {actions.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Corrective actions assigned to you</h2>
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                {actions.map((a, i) => (
                  <ActionRow key={a.id} a={a} first={i === 0}
                    right={a.dueDate
                      ? <span style={{ fontSize: text.hint.fontSize, fontWeight: a.overdue ? 600 : 400, color: a.overdue ? theme.color.dangerText : theme.color.muted }}>{a.overdue ? "Overdue: was due" : "Due"} {fmtDateOnly(a.dueDate)}</span>
                      : <span style={text.hint}>No due date</span>} />
                ))}
              </div>
            </section>
          )}

          {steps.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Steps given to you</h2>
              <p style={{ margin: "0 0 10px", ...text.hint, maxWidth: 760 }}>
                Parts of a corrective action someone has asked you to carry out. Mark each one done when it is; the action's owner closes the action itself.
              </p>
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                {steps.map((t, i) => <StepRow key={t.id} t={t} first={i === 0} onDone={load} />)}
              </div>
            </section>
          )}

          {reviews.length > 0 && (
            <section>
              <h2 style={sectionTitle}>Effectiveness reviews due</h2>
              <p style={{ margin: "0 0 10px", ...text.hint }}>
                Actions you own or verified. Check the problem has not come back and record what you found.
              </p>
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                {reviews.map((a, i) => (
                  <ActionRow key={a.id} a={a} first={i === 0}
                    right={<Pill tone={a.standing === "overdue" ? "danger" : "warning"}>{a.standing === "overdue" ? "Overdue" : "Due"} {fmtDay(a.effectivenessDueAt)}</Pill>} />
                ))}
              </div>
            </section>
          )}

        </div>
      )}
    </div>
  );
}

// ── Policy exceptions ─────────────────────────────────────────────────────

function PolicyExceptionCard({ e, onDone }: { e: PolicyExceptionTask; onDone: () => Promise<void> }) {
  const [deciding, setDeciding] = useState<"approve" | "reject" | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!deciding) return;
    if (deciding === "reject" && note.trim().length < 3) { setError("Say why you are rejecting it."); return; }
    setBusy(true); setError(null);
    try { await api.post(`/me/tasks/policy-exceptions/${e.id}`, { decision: deciding, note: note.trim() || null }); await onDone(); }
    catch (err) { setError(apiErrorMessage(err, "Could not record your decision")); setBusy(false); }
  }

  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={text.itemTitle}><span style={{ ...text.ref, marginRight: 6 }}>{e.ref}</span>{e.title}</div>
          <div style={{ ...text.hint, marginTop: 2 }}>
            {e.renewal ? "Renewal of an exception to" : "Exception to"} {e.policyTitle}{e.ruleReference ? ` (${e.ruleReference})` : ""} · {fmtDateOnly(e.startsAt)} to {fmtDateOnly(e.expiresAt)}{e.requestedBy ? ` · asked by ${e.requestedBy}` : ""}
          </div>
        </div>
        {!deciding && (
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={btnPrimary} onClick={() => { setDeciding("approve"); setError(null); }}>Approve</button>
            <button type="button" style={dangerBtn} onClick={() => { setDeciding("reject"); setError(null); }}>Reject</button>
          </div>
        )}
      </div>
      <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "8px 20px", margin: "12px 0 0" }}>
        <div><dt style={text.label}>Why the policy cannot be followed</dt><dd style={{ margin: 0, ...text.body, whiteSpace: "pre-wrap" }}>{e.justification}</dd></div>
        <div><dt style={text.label}>Compensating controls</dt><dd style={{ margin: 0, ...text.body, whiteSpace: "pre-wrap" }}>{e.compensatingControls}</dd></div>
        <div><dt style={text.label}>Covers</dt><dd style={{ margin: 0, ...text.body }}>{e.covers.length ? e.covers.join(", ") : "The whole organisation"}</dd></div>
        <div><dt style={text.label}>Risk accepted</dt><dd style={{ margin: 0, ...text.body }}>{e.risk.ref ? `${e.risk.ref} ` : ""}{e.risk.title}{e.risk.score ? ` (score ${e.risk.score})` : ""}</dd></div>
      </dl>
      {e.selfApproved && (
        <div style={{ fontSize: text.hint.fontSize, color: theme.color.warningText, background: theme.color.warningBg, padding: "6px 10px", borderRadius: 3, marginTop: 10 }}>
          You are the organisation's only member, so you approve your own request. It is recorded as such.
        </div>
      )}
      {deciding && (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <Field label={deciding === "approve" ? "Note (optional)" : "Why are you rejecting it?"}>
            <textarea style={textarea} rows={2} maxLength={2000} value={note} onChange={(ev) => setNote(ev.target.value)} />
          </Field>
          {error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy} style={{ ...(deciding === "approve" ? btnPrimary : dangerBtn), opacity: busy ? 0.6 : 1 }} onClick={submit}>{deciding === "approve" ? "Approve and accept the risk" : "Reject"}</button>
            <button type="button" style={secondaryBtn} onClick={() => { setDeciding(null); setError(null); }}>Back</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Access reviews ────────────────────────────────────────────────────────

function AccessReviewRow({ r, first, onDone }: { r: AccessReviewTask; first: boolean; onDone: () => Promise<void> }) {
  const [deciding, setDeciding] = useState<"keep" | "remove" | null>(null);
  const [reason, setReason] = useState("");
  const [leaver, setLeaver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!deciding) return;
    if (reason.trim().length < 3) { setError(deciding === "keep" ? "Say why they still need this access." : "Say why this access should be removed."); return; }
    setBusy(true); setError(null);
    try { await api.post(`/me/tasks/access-reviews/${r.id}`, { decision: deciding, reason: reason.trim(), ...(deciding === "remove" && leaver ? { leaver: true } : {}) }); await onDone(); }
    catch (e) { setError(apiErrorMessage(e, "Could not record your answer")); setBusy(false); }
  }

  return (
    <div style={{ padding: "12px 16px", borderTop: first ? "none" : "1px solid var(--c-border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={text.itemTitle}>{r.principalName} <span style={{ fontWeight: 400, color: theme.color.muted }}>in</span> {r.resourceName}</div>
          <div style={{ ...text.hint, marginTop: 2 }}>
            {r.resourceKind === "group" ? "Group" : "Application"} in {r.sourceLabel}{r.principalEmail && r.principalEmail !== r.principalName ? ` · ${r.principalEmail}` : ""} · asked as {r.askedAs.toLowerCase()}
          </div>
          <div style={{ ...text.hint, marginTop: 2, color: r.overdue ? theme.color.dangerText : theme.color.muted, fontWeight: r.overdue ? 600 : 400 }}>
            <span style={{ ...text.ref, marginRight: 6 }}>{r.campaign.ref}</span>{r.overdue ? "Overdue: was due" : "Due"} {fmtDateOnly(r.campaign.dueAt)}
          </div>
        </div>
        {!deciding && (
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={btnPrimary} onClick={() => { setDeciding("keep"); setError(null); }}>Keep</button>
            <button type="button" style={dangerBtn} onClick={() => { setDeciding("remove"); setError(null); }}>Remove</button>
          </div>
        )}
      </div>
      {r.selfReviewed && (
        <div style={{ fontSize: text.hint.fontSize, color: theme.color.warningText, background: theme.color.warningBg, padding: "6px 10px", borderRadius: 3, marginTop: 10 }}>
          This is your own access and no other administrator can review it. Your answer is recorded as self-reviewed.
        </div>
      )}
      {deciding && (
        <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
          <Field label={deciding === "keep" ? "Why do they still need it?" : "Why should it be removed?"}>
            <textarea style={textarea} rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {deciding === "remove" && <Check checked={leaver} onChange={setLeaver} label="They have left the organisation" help="Starts a leaver checklist instead of a single removal." />}
          {error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy} style={{ ...(deciding === "keep" ? btnPrimary : dangerBtn), opacity: busy ? 0.6 : 1 }} onClick={submit}>{deciding === "keep" ? "Keep access" : "Remove access"}</button>
            <button type="button" style={secondaryBtn} onClick={() => { setDeciding(null); setError(null); }}>Back</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Technical scopes ──────────────────────────────────────────────────────

function ScopeReviewCard({ r, onDone }: { r: ScopeReviewTask; onDone: () => Promise<void> }) {
  const [reading, setReading] = useState(false);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const s = r.scope;

  async function toggleRead() {
    if (reading) { setReading(false); return; }
    setReading(true);
    if (markdown !== null) return;
    setLoadingDoc(true); setError(null);
    try { setMarkdown((await api.get<{ markdown: string; version: number }>(`/me/tasks/scope-reviews/${r.id}/document`)).markdown); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the document")); setReading(false); }
    setLoadingDoc(false);
  }
  async function download() {
    setError(null);
    try { saveBlob(await apiBlob(`/me/tasks/scope-reviews/${r.id}/document`, { format: "docx" }), `${fileNameSafe(`${s.ref} ${s.title} v${r.version}`)}.docx`); }
    catch { setError("Could not download the Word document"); }
  }
  async function decide(decision: "confirm" | "request_changes") {
    if (decision === "request_changes" && note.trim().length < 3) { setError("Say what needs changing."); return; }
    setBusy(true); setError(null);
    try { await api.post(`/me/tasks/scope-reviews/${r.id}`, { decision, ...(note.trim() ? { note: note.trim() } : {}) }); await onDone(); }
    catch (e) { setError(apiErrorMessage(e, "Could not record your answer")); setBusy(false); }
  }

  const dates = s.plannedStart || s.plannedEnd
    ? `${s.plannedStart ? fmtDateOnly(s.plannedStart) : "?"} to ${s.plannedEnd ? fmtDateOnly(s.plannedEnd) : "?"}`
    : null;

  return (
    <div style={{ ...card, padding: 18 }}>
      <div style={text.itemTitle}>
        <span style={{ ...text.ref, color: theme.color.faint, marginRight: 8 }}>{s.ref}</span>
        {s.title} v{r.version}
      </div>
      <div style={{ ...text.hint, marginTop: 2 }}>
        {s.workTypeLabel}{s.performer ? ` · carried out by ${s.performer}` : ""}{dates ? ` · ${dates}` : ""}
      </div>
      <div style={{ ...text.hint, marginTop: 2 }}>
        {r.requestedBy ? `Asked by ${r.requestedBy}` : "Asked"} on {fmtDay(r.requestedAt)}
      </div>
      {r.note && <div style={{ ...text.body, marginTop: 10, whiteSpace: "pre-wrap" }}>{r.note}</div>}
      {r.selfReviewed && (
        <div style={{ fontSize: text.hint.fontSize, color: theme.color.warningText, background: theme.color.warningBg, padding: "6px 10px", borderRadius: 3, marginTop: 10 }}>
          You asked for this review yourself. It will be recorded as self-reviewed.
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button type="button" style={secondaryBtn} onClick={toggleRead}>{reading ? "Hide the document" : "Read the document"}</button>
        <button type="button" style={secondaryBtn} onClick={download}><Download size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Download Word</button>
        {!requesting && <>
          <button type="button" disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} onClick={() => decide("confirm")}><CheckCircle2 size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Confirm</button>
          <button type="button" disabled={busy} style={dangerBtn} onClick={() => setRequesting(true)}>Request changes</button>
        </>}
      </div>
      {reading && (
        <div style={{ marginTop: 12, padding: "12px 16px", border: `1px solid ${theme.color.border}`, borderRadius: 3, maxHeight: 520, overflowY: "auto", background: theme.color.panel }}>
          {loadingDoc ? <div style={text.hint}>Loading…</div> : markdown !== null && <MarkdownDocument content={markdown} />}
        </div>
      )}
      {requesting && (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          <Field label="What needs changing? *"><textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} maxLength={2000} /></Field>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy || note.trim().length < 3} style={{ ...dangerBtn, opacity: busy || note.trim().length < 3 ? 0.6 : 1 }} onClick={() => decide("request_changes")}>Request changes</button>
            <button type="button" style={secondaryBtn} onClick={() => { setRequesting(false); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}
      {error && <div style={{ ...text.body, color: theme.color.dangerText, marginTop: 10 }}>{error}</div>}
    </div>
  );
}

// ── Corrective actions ────────────────────────────────────────────────────

const ACTION_STATUS_LABELS: Record<string, string> = {
  proposed: "Proposed", approved: "Approved", in_progress: "In progress", blocked: "Blocked",
  completed: "Completed", verified: "Verified", cancelled: "Cancelled",
};

function ActionRow({ a, first, right }: { a: ActionTask; first: boolean; right: React.ReactNode }) {
  return (
    <Link
      to={a.href}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "12px 16px", textDecoration: "none", borderTop: first ? "none" : `1px solid ${theme.color.border}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={text.itemTitle}>
          {a.displayId !== null && <span style={{ ...text.ref, color: theme.color.faint, marginRight: 8 }}>CA-{a.displayId}</span>}
          {a.title}
        </div>
        <div style={{ ...text.hint, marginTop: 2 }}>
          {ACTION_STATUS_LABELS[a.status] ?? a.status}{a.priority !== "medium" ? ` · ${a.priority} priority` : ""}
        </div>
      </div>
      {right}
    </Link>
  );
}

function StepRow({ t, first, onDone }: { t: StepTask; first: boolean; onDone: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function done() {
    setBusy(true); setError(null);
    try { await api.post(`/me/tasks/action-steps/${t.id}/complete`, { confirm: true }); await onDone(); }
    catch (e) { setError(apiErrorMessage(e, "Could not mark the step done")); setBusy(false); }
  }
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 16px", borderTop: first ? "none" : `1px solid ${theme.color.border}` }}>
      <div style={{ minWidth: 0, flex: "1 1 320px" }}>
        <div style={{ ...text.body, fontWeight: 600 }}>{t.title}</div>
        <div style={{ ...text.hint, marginTop: 2 }}>
          Step {t.stepNumber} of {t.stepCount} ·{" "}
          <Link to={t.href}>{t.action.displayId !== null ? `CA-${t.action.displayId} ` : ""}{t.action.title}</Link>
        </div>
        {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 4 }}>{error}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {t.dueDate
          ? <span style={{ fontSize: text.hint.fontSize, fontWeight: t.overdue ? 600 : 400, color: t.overdue ? theme.color.dangerText : theme.color.muted }}>{t.overdue ? "Overdue: was due" : "Due"} {fmtDateOnly(t.dueDate)}</span>
          : <span style={text.hint}>No due date</span>}
        <button type="button" disabled={busy} onClick={() => void done()} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Mark done
        </button>
      </div>
    </div>
  );
}

// ── Policy acknowledgement ────────────────────────────────────────────────

function AckCard({ a, onDone }: { a: AckTask; onDone: () => Promise<void> }) {
  const [opened, setOpened] = useState(!!a.openedAt);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = a.status === "acknowledged";

  async function open() {
    setBusy(true); setError(null);
    try { await openLink(`/me/tasks/acknowledgements/${a.id}/open`); setOpened(true); }
    catch (e) { setError(apiErrorMessage(e, "Could not open the policy")); }
    setBusy(false);
  }
  async function confirm() {
    setBusy(true); setError(null);
    try { await api.post(`/me/tasks/acknowledgements/${a.id}`, { confirm: true }); await onDone(); }
    catch (e) { setError(apiErrorMessage(e, "Could not record your acknowledgement")); setBusy(false); }
  }

  return (
    <div style={{ ...card, padding: 18, opacity: done ? 0.7 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={text.itemTitle}>{a.campaign.documentTitle} v{a.campaign.documentVersion}</div>
          {a.campaign.title !== `Acknowledge ${a.campaign.documentTitle}` && <div style={{ ...text.hint, marginTop: 2 }}>{a.campaign.title}</div>}
        </div>
        {done
          ? <StatusTag tone="met">Acknowledged {fmtDay(a.acknowledgedAt)}</StatusTag>
          : <span style={{ fontSize: text.hint.fontSize, fontWeight: a.overdue ? 600 : 400, color: a.overdue ? theme.color.dangerText : theme.color.muted }}>{a.overdue ? "Overdue: was due" : "Due"} {fmtDateOnly(a.campaign.dueAt)}</span>}
      </div>
      {a.campaign.message && !done && <div style={{ ...text.body, marginTop: 10, whiteSpace: "pre-wrap" }}>{a.campaign.message}</div>}

      {done ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ ...text.label, marginBottom: 4 }}>You confirmed</div>
          <div style={{ padding: "10px 12px", border: "1px solid var(--c-border)", borderRadius: 3, fontSize: text.body.fontSize, color: theme.color.muted }}>{a.statement}</div>
        </div>
      ) : (
        <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
          <div>
            <button type="button" disabled={busy || !a.documentAvailable} onClick={open} style={{ ...(opened ? secondaryBtn : btnPrimary), opacity: busy || !a.documentAvailable ? 0.6 : 1 }}>
              <ExternalLink size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Open the policy
            </button>
            {!a.documentAvailable && <span style={{ ...text.hint, marginLeft: 10 }}>The file is no longer on file. Ask whoever sent it for the current version.</span>}
            {!opened && a.documentAvailable && <span style={{ ...text.hint, marginLeft: 10 }}>Open and read it first. You can confirm once it has been opened.</span>}
          </div>
          {opened && (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ padding: "10px 12px", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, fontSize: text.body.fontSize, color: theme.color.text }}>{a.statement}</div>
              <Check checked={confirmed} onChange={setConfirmed} label="I confirm the statement above" />
              <div><button type="button" disabled={busy || !confirmed} onClick={confirm} style={{ ...btnPrimary, opacity: busy || !confirmed ? 0.6 : 1 }}>Confirm</button></div>
            </div>
          )}
        </div>
      )}
      {error && <div style={{ ...text.body, color: theme.color.dangerText, marginTop: 10 }}>{error}</div>}
    </div>
  );
}

// ── Training ───────────────────────────────────────────────────────────────

const STANDING_PILL: Record<Standing, { tone: "success" | "warning" | "danger" | "accent" | "muted"; label: string }> = {
  completed:             { tone: "success", label: "Completed" },
  excused:               { tone: "muted",   label: "Excused" },
  awaiting_confirmation: { tone: "accent",  label: "Waiting for confirmation" },
  overdue:               { tone: "danger",  label: "Overdue" },
  due_soon:              { tone: "warning", label: "Due soon" },
  pending:               { tone: "muted",   label: "To do" },
};

function TrainingCard({ t, onDone }: { t: TrainingTask; onDone: () => Promise<void> }) {
  const [recording, setRecording] = useState(false);
  const [score, setScore] = useState("");
  const [note, setNote] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const a = t.assignment;
  const pill = STANDING_PILL[t.standing] ?? STANDING_PILL.pending;
  const adminConfirm = a.completionMode === "admin_confirm";

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await api.post<{ ok: boolean; status: "submitted" | "completed" }>(`/me/tasks/training/${t.id}/complete`, {
        confirm: true, score: a.passMark != null && score.trim() !== "" ? Number(score) : null, attestation: note.trim() || null,
      });
      setRecording(false);
      await onDone();
    } catch (err) { setError(apiErrorMessage(err, "Could not record the course")); }
    setBusy(false);
  }

  return (
    <div style={{ ...card, padding: 18, opacity: t.status === "completed" ? 0.7 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={text.itemTitle}>{a.title}</div>
          <div style={{ ...text.hint, marginTop: 2 }}>
            {labelOf(TRAINING_KINDS, a.kind)}{a.provider ? ` · ${a.provider}` : ""}{t.cycle > 1 ? ` · renewal ${t.cycle}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {t.status === "completed"
            ? <StatusTag tone="met">Completed {fmtDay(t.completedAt)}</StatusTag>
            : <>
                <span style={{ fontSize: text.hint.fontSize, color: t.standing === "overdue" ? theme.color.dangerText : theme.color.muted }}>Due {fmtDateOnly(t.dueAt)}</span>
                <Pill tone={pill.tone}>{pill.label}</Pill>
              </>}
        </div>
      </div>
      {a.description && t.status !== "completed" && <div style={{ ...text.body, marginTop: 10, whiteSpace: "pre-wrap" }}>{a.description}</div>}

      {t.status === "pending" && (
        <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {isHttps(a.url) && (
              <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ ...secondaryBtn, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <ExternalLink size={13} />Open the course
              </a>
            )}
            {!recording && <button type="button" style={btnPrimary} onClick={() => setRecording(true)}>Record as done</button>}
            {adminConfirm && <span style={text.hint}>An administrator will confirm it before it counts.</span>}
          </div>
          {recording && (
            <form onSubmit={submit} style={{ display: "grid", gap: 12, padding: 14, border: "1px solid var(--c-border)", borderRadius: 3 }}>
              {a.passMark != null && (
                <Field label="Your score (%) *" help={`Pass mark ${a.passMark}%`}>
                  <input type="number" required min={0} max={100} value={score} onChange={(e) => setScore(e.target.value)} style={{ ...input, maxWidth: 140 }} />
                </Field>
              )}
              <Field label="Note" help="Optional: for example, the certificate number or when you took it.">
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} maxLength={1000} />
              </Field>
              <Check checked={confirmed} onChange={setConfirmed} label="I confirm I have completed this course" />
              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" disabled={busy || !confirmed} style={{ ...btnPrimary, opacity: busy || !confirmed ? 0.6 : 1 }}>{busy ? "Saving…" : "Record"}</button>
                <button type="button" style={secondaryBtn} onClick={() => { setRecording(false); setError(null); }}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}
      {t.status === "submitted" && (
        <div style={{ marginTop: 12, ...text.body, color: theme.color.muted }}>
          You recorded this on {fmtDay(t.submittedAt)}{t.score != null ? ` with a score of ${t.score}%` : ""}. Waiting for an administrator to confirm it.
        </div>
      )}
      {t.status === "completed" && t.score != null && <div style={{ marginTop: 8, ...text.hint }}>Score {t.score}%</div>}
      {error && <div style={{ ...text.body, color: theme.color.dangerText, marginTop: 10 }}>{error}</div>}
    </div>
  );
}

// ── Approvals ──────────────────────────────────────────────────────────────

function ApprovalCard({ a, onDone }: { a: ApprovalTask; onDone: () => Promise<void> }) {
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function open() {
    setError(null);
    try { await openLink(`/me/tasks/approvals/${a.id}/open`); }
    catch (e) { setError(apiErrorMessage(e, "Could not open the policy")); }
  }
  async function decide(decision: "approve" | "reject") {
    if (decision === "reject" && note.trim().length < 3) { setError("Say why you are sending it back."); return; }
    setBusy(true); setError(null);
    try { await api.post(`/me/tasks/approvals/${a.id}/decide`, { decision, note: note.trim() || null }); await onDone(); }
    catch (e) { setError(apiErrorMessage(e, "Could not record your decision")); setBusy(false); }
  }

  return (
    <div style={{ ...card, padding: 18 }}>
      <div style={text.itemTitle}>{a.documentTitle} v{a.documentVersion}</div>
      <div style={{ ...text.hint, marginTop: 2 }}>
        {a.requestedBy ? `Asked by ${a.requestedBy}` : "Asked"} on {fmtDay(a.createdAt)} · review again after {a.reviewIntervalMonths} months
      </div>
      {a.requestNote && <div style={{ ...text.body, marginTop: 10, whiteSpace: "pre-wrap" }}>{a.requestNote}</div>}
      {a.selfApproved && (
        <div style={{ fontSize: text.hint.fontSize, color: theme.color.warningText, background: theme.color.warningBg, padding: "6px 10px", borderRadius: 3, marginTop: 10 }}>
          You asked for this yourself. As the organisation's only member it will be recorded as self-approved.
        </div>
      )}
      <div style={{ ...text.hint, marginTop: 10 }}>
        Approving says this version is the organisation's policy. It can then be sent to staff to acknowledge.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button type="button" style={secondaryBtn} disabled={!a.documentAvailable} onClick={open}><ExternalLink size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Open the policy</button>
        {!rejecting && <>
          <button type="button" disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }} onClick={() => decide("approve")}><CheckCircle2 size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Approve</button>
          <button type="button" disabled={busy} style={dangerBtn} onClick={() => setRejecting(true)}>Send back</button>
        </>}
      </div>
      {rejecting && (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          <Field label="Why are you sending it back? *"><textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} maxLength={2000} /></Field>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy || note.trim().length < 3} style={{ ...dangerBtn, opacity: busy || note.trim().length < 3 ? 0.6 : 1 }} onClick={() => decide("reject")}>Send back</button>
            <button type="button" style={secondaryBtn} onClick={() => { setRejecting(false); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}
      {error && <div style={{ ...text.body, color: theme.color.dangerText, marginTop: 10 }}>{error}</div>}
    </div>
  );
}

// ── Control attestations ──────────────────────────────────────────────────

function AttestationCard({ a, onDone }: { a: AttestationTask; onDone: () => Promise<void> }) {
  const [refusing, setRefusing] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(outcome: "operating" | "not_operating") {
    setBusy(true); setError(null);
    try { await api.post(`/me/tasks/attestations/${encodeURIComponent(a.controlId)}`, { outcome, note: note.trim() || null }); await onDone(); }
    catch (err) { setError(apiErrorMessage(err, "Could not record your attestation")); setBusy(false); }
  }

  const due = a.standing === "lapsed" ? `Overdue since ${fmtDateOnly(a.nextDueAt)}` : a.standing === "not_operating" ? "You said it is not operating: attest again once it works" : `Due ${fmtDateOnly(a.nextDueAt)}`;
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={text.itemTitle}><PortalLink to={a.href} style={{ color: theme.color.text }}>{a.controlLabel}</PortalLink></div>
          <div style={{ ...text.hint, marginTop: 2 }}>{due} · every {a.cycleMonths} months{a.last ? ` · last attested ${fmtDateOnly(a.last.attestedAt)}` : ""}</div>
        </div>
        {!refusing && (
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy} style={btnPrimary} onClick={() => void submit("operating")}>It operates as described</button>
            <button type="button" disabled={busy} style={dangerBtn} onClick={() => { setRefusing(true); setError(null); }}>It does not</button>
          </div>
        )}
      </div>
      {refusing && (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <Field label="What is not working?">
            <textarea style={textarea} rows={2} maxLength={2000} value={note} onChange={(ev) => setNote(ev.target.value)} />
          </Field>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy} style={dangerBtn} onClick={() => void submit("not_operating")}>Record it is not operating</button>
            <button type="button" style={secondaryBtn} onClick={() => { setRefusing(false); setError(null); }}>Back</button>
          </div>
        </div>
      )}
      {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// ── Changes ───────────────────────────────────────────────────────────────

function ChangeDecisionCard({ c, onDone }: { c: ChangeTask; onDone: () => Promise<void> }) {
  const [deciding, setDeciding] = useState<"approve" | "reject" | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!deciding) return;
    if (deciding === "reject" && note.trim().length < 3) { setError("Say why you are rejecting it."); return; }
    setBusy(true); setError(null);
    try { await api.post(`/me/tasks/changes/${c.id}`, { decision: deciding, note: note.trim() || null }); await onDone(); }
    catch (err) { setError(apiErrorMessage(err, "Could not record your decision")); setBusy(false); }
  }

  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={text.itemTitle}><span style={{ ...text.ref, marginRight: 6 }}>{c.ref}</span>{c.title}</div>
          <div style={{ ...text.hint, marginTop: 2 }}>
            {c.changeTypeLabel} change
            {c.retrospective && c.implementedAt ? ` · made ${fmtDateTime(c.implementedAt)}, before approval` : c.plannedDate ? ` · planned for ${fmtDateOnly(c.plannedDate)}` : ""}
            {c.requestedBy ? ` · asked by ${c.requestedBy}` : ""}
          </div>
        </div>
        {!deciding && (
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={btnPrimary} onClick={() => { setDeciding("approve"); setError(null); }}>{c.retrospective ? "Approve after the fact" : "Approve"}</button>
            <button type="button" style={dangerBtn} onClick={() => { setDeciding("reject"); setError(null); }}>{c.retrospective ? "Refuse" : "Reject"}</button>
          </div>
        )}
      </div>
      <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "8px 20px", margin: "12px 0 0" }}>
        <div><dt style={text.label}>What and why</dt><dd style={{ margin: 0, ...text.body, whiteSpace: "pre-wrap" }}>{c.description}</dd></div>
        <div><dt style={text.label}>Rollback plan</dt><dd style={{ margin: 0, ...text.body, whiteSpace: "pre-wrap" }}>{c.rollbackPlan || "None given (a standard change)"}</dd></div>
        <div><dt style={text.label}>Assets it touches</dt><dd style={{ margin: 0, ...text.body }}>{c.assets.length ? c.assets.join(", ") : "None named"}</dd></div>
      </dl>
      {c.selfApproved && (
        <div style={{ fontSize: text.hint.fontSize, color: theme.color.warningText, background: theme.color.warningBg, padding: "6px 10px", borderRadius: 3, marginTop: 10 }}>
          You are the organisation's only member, so you approve your own change. It is recorded as such.
        </div>
      )}
      {deciding && (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <Field label={deciding === "approve" ? "Note (optional)" : c.retrospective ? "Why should it be rolled back?" : "Why are you rejecting it?"}>
            <textarea style={textarea} rows={2} maxLength={2000} value={note} onChange={(ev) => setNote(ev.target.value)} />
          </Field>
          {deciding === "reject" && c.retrospective && <div style={text.hint}>Refusing a change already made marks it failed, to be rolled back and reviewed.</div>}
          {error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy} style={{ ...(deciding === "approve" ? btnPrimary : dangerBtn), opacity: busy ? 0.6 : 1 }} onClick={submit}>{deciding === "approve" ? "Approve" : c.retrospective ? "Refuse" : "Reject"}</button>
            <button type="button" style={secondaryBtn} onClick={() => { setDeciding(null); setError(null); }}>Back</button>
          </div>
        </div>
      )}
    </div>
  );
}
